import {
  Box,
  Button,
  Container,
  SpaceBetween,
  FormField,
  ColumnLayout,
} from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import constants from '../../../common/constants';
import { ContentSectionKey } from '../../../model/ContentSchemaSection';
import { Troubleshooting } from '@amzn/awscat-pattern-library-service-model';
import EditorInput from '../../../editor/EditorInput';
import { TiptapOutputType } from '@amzn/awscat-editor';

export interface TroubleshootingEditorProps {
  contentId: string;
  sectionId: string;
  troubleshooting?: Troubleshooting;
  onAddConfirmed?: (troubleshooting: Troubleshooting) => void;
  onEdit?: (troubleshooting: Troubleshooting) => void;
  onCancel?: () => void;
  direction?: 'horizontal' | 'vertical';
}

const TroubleshootingEditor = (props: TroubleshootingEditorProps) => {
  const [troubleshooting, setTroubleshooting] = useState(
    props.troubleshooting || Troubleshooting.create('', ''),
  );

  useEffect(() => {
    setTroubleshooting(props.troubleshooting || Troubleshooting.create('', ''));
  }, [props.contentId, props.sectionId, props.troubleshooting]);

  const onContentChanged = (
    sectionId: string,
    key: ContentSectionKey,
    newContent: TiptapOutputType,
  ) => {
    if (troubleshooting) {
      // @ts-expect-error TS7053 - strict migration
      troubleshooting[sectionId.split('.')[1]] = newContent;
      setTroubleshooting(troubleshooting);
    }
  };

  const cancel = () => {
    props.onCancel?.();
  };

  const add = () => {
    props.onAddConfirmed?.(troubleshooting);
  };

  const issueEditor = (
    <FormField
      key="issue"
      label={'Describe your issue *'}
      description="Specify the error message or problem users might encounter when they implement this pattern."
      stretch
    >
      <EditorInput
        contentId={props.contentId}
        sectionId={troubleshooting && `${troubleshooting.id}.issue`}
        data={troubleshooting?.issue}
        onContentChanged={onContentChanged}
        readonly={false}
      />
    </FormField>
  );

  const solutionEditor = (
    <FormField
      key="solution"
      label={'Describe your solution *'}
      description="Explain the possible causes of the error message or problem, describe fixes or workarounds, and provide additional guidance to help users avoid similar issues."
      stretch
    >
      <EditorInput
        contentId={props.contentId}
        sectionId={troubleshooting && `${troubleshooting.id}.solution`}
        data={troubleshooting?.solution}
        onContentChanged={onContentChanged}
        readonly={false}
      />
    </FormField>
  );

  return (
    <Container>
      <SpaceBetween direction={'vertical'} size={'xxl'}>
        {props.direction === 'vertical' ? (
          [issueEditor, solutionEditor]
        ) : (
          <ColumnLayout columns={2}>
            {issueEditor}
            {solutionEditor}
          </ColumnLayout>
        )}
        <Box margin={{ top: 's' }} float={'right'}>
          <Button variant={'link'} onClick={cancel}>
            {constants.CANCEL_BUTTON_TEXT}
          </Button>
          <Button onClick={add}>{constants.CONFIRM_BUTTON_TEXT}</Button>
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default TroubleshootingEditor;
